class BundleProductCard {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.sectionContainer = document.querySelector(`#BundleSection-${sectionId}`);
    if (!this.sectionContainer) return;

    this.bundleDiscountPercentage = this.getDiscountPercentage('bundleDiscountPercentage', 'discountPercentage');
    this.comboDiscountPercentage = this.getDiscountPercentage('comboDiscountPercentage', 'bundleDiscountPercentage');
    this.requiredWrappers = this.sectionContainer.querySelectorAll('.bundle-card-wrapper--required');
    this.optionalWrappers = this.sectionContainer.querySelectorAll('.bundle-list-item--optional');
    this.addToCartBtn = this.sectionContainer.querySelector('[data-bundle-add-to-cart]');
    this.originalTotalEls = this.sectionContainer.querySelectorAll('.original-total');
    this.discountedTotalEls = this.sectionContainer.querySelectorAll('.discounted-total');
    this.itemCountEl = this.sectionContainer.querySelector('.bundle-item-count');
    this.savingsBadgeEl = this.sectionContainer.querySelector('.bundle-savings-badge');
    this.itemsCountTemplate = this.sectionContainer.dataset.itemsCountTemplate || '[count] items';
    this.cartErrorText = this.sectionContainer.dataset.cartErrorText || 'Unable to add the bundle to cart. Please try again.';
    this.moneySample = this.sectionContainer.dataset.moneySample || '';
    this.moneySampleCents = Number(this.sectionContainer.dataset.moneySampleCents) || 123456;
    this.productDiscountText = this.sectionContainer.dataset.productDiscountText || 'Save [discount]%';
    this.accessoriesDiscountText = this.sectionContainer.dataset.accessoriesDiscountText || 'Enjoy [discount]% OFF';

    this.init();
  }

  getDiscountPercentage(primaryKey, fallbackKey) {
    const primaryValue = Number.parseFloat(this.sectionContainer.dataset[primaryKey]);
    if (Number.isFinite(primaryValue)) {
      return Math.min(Math.max(primaryValue, 0), 100);
    }

    const fallbackValue = Number.parseFloat(this.sectionContainer.dataset[fallbackKey]);
    return Number.isFinite(fallbackValue) ? Math.min(Math.max(fallbackValue, 0), 100) : 0;
  }

  init() {
    this.requiredWrappers.forEach(wrapper => {
      wrapper.variantData = this.getVariantData(wrapper);
    });

    this.optionalWrappers.forEach(wrapper => {
      wrapper.variantData = this.getVariantData(wrapper);
      this.syncOptionalSelection(wrapper, false);
    });

    this.updateBundleUI();
    this.bindEvents();
  }

  bindEvents() {
    this.sectionContainer.querySelectorAll('[data-bundle-color-swatches] input[type="radio"]').forEach(input => {
      const wrapper = input.closest('.bundle-card-wrapper, .bundle-list-item--optional');
      if (!wrapper) return;

      input.addEventListener('change', () => this.handleOptionChange(wrapper, input));
    });

    this.optionalWrappers.forEach(wrapper => {
      const checkbox = wrapper.querySelector('.bundle-selection-checkbox');

      if (checkbox) {
        checkbox.addEventListener('change', event => {
          event.stopPropagation();
          this.syncOptionalSelection(wrapper);
          this.updateBundleUI();
        });
      }

      wrapper.addEventListener('click', event => {
        if (event.target.closest('select, input, label, button, a')) return;
        this.toggleOptionalSelection(wrapper);
      });
    });

    if (this.addToCartBtn) {
      this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
    }
  }

  getVariantData(wrapper) {
    const dataElement = wrapper.querySelector('.bundle-variant-data');
    if (dataElement) {
      try {
        const variants = JSON.parse(dataElement.textContent);
        if (Array.isArray(variants)) return variants;
      } catch (error) {
        console.error('Unable to parse bundle variant data.', error);
      }
    }

    return [];
  }

  syncOptionalSelection(wrapper, updateCheckbox = true) {
    const checkbox = wrapper.querySelector('.bundle-selection-checkbox');
    const isSelected = checkbox ? checkbox.checked : wrapper.dataset.selected === 'true';

    wrapper.dataset.selected = String(isSelected);
    if (checkbox && updateCheckbox) checkbox.checked = isSelected;
  }

  toggleOptionalSelection(wrapper) {
    const checkbox = wrapper.querySelector('.bundle-selection-checkbox');
    if (checkbox) checkbox.checked = !checkbox.checked;

    this.syncOptionalSelection(wrapper);
    this.updateBundleUI();
  }

  handleOptionChange(wrapper, input) {
    const variantData = wrapper.variantData || this.getVariantData(wrapper);
    if (!variantData.length) return;

    const selectedOptions = this.getSelectedOptions(wrapper);
    const selectedPosition = Number(input.closest('[data-option-position]')?.dataset.optionPosition) - 1;
    const matchingVariant = this.findMatchingVariant(variantData, selectedOptions, selectedPosition, input.value);

    if (matchingVariant) {
      this.applyVariant(wrapper, matchingVariant);
    }
  }

  getSelectedOptions(wrapper) {
    const selectedOptions = [];

    wrapper.querySelectorAll('[data-option-position]').forEach(fieldset => {
      const selectedInput = fieldset.querySelector('input[type="radio"]:checked');
      const optionIndex = Number(fieldset.dataset.optionPosition) - 1;
      if (selectedInput && optionIndex >= 0) selectedOptions[optionIndex] = selectedInput.value;
    });

    return selectedOptions;
  }

  findMatchingVariant(variants, selectedOptions, changedIndex, changedValue) {
    const exactMatch = variants.find(variant => {
      return variant.options.every((value, index) => !selectedOptions[index] || value === selectedOptions[index]);
    });

    if (exactMatch) return exactMatch;

    return variants.find(variant => {
      return variant.options[changedIndex] === changedValue;
    });
  }

  applyVariant(wrapper, variant) {
    wrapper.dataset.defaultVariantId = variant.id;
    wrapper.dataset.defaultVariantPrice = variant.price;
    wrapper.dataset.defaultVariantCompareAtPrice = variant.compare_at_price || 0;

    const image = wrapper.querySelector('.bundle-item-image');
    if (image && variant.image) {
      image.src = variant.image;
      image.removeAttribute('srcset');
    }

    if (wrapper.querySelector('[data-option-position]')) {
      this.setOptionState(wrapper, variant);
    }

    this.updateBundleUI();
  }

  setOptionState(wrapper, variant) {
    wrapper.querySelectorAll('[data-option-position]').forEach(fieldset => {
      const optionIndex = Number(fieldset.dataset.optionPosition) - 1;
      const value = variant.options[optionIndex];
      const input = Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        .find(radio => radio.value === value);

      if (input) input.checked = true;

      const valueElement = fieldset.querySelector('.js-bundle-option-value');
      if (valueElement) valueElement.textContent = value || '';
    });
  }

  formatMoney(cents) {
    const formattedFromSample = this.formatMoneyFromSample(cents);
    if (formattedFromSample) return formattedFromSample;

    const moneySettings = window.themeVariables?.settings || {};
    const moneyFormat = moneySettings.currencyCodeEnabled
      ? moneySettings.moneyWithCurrencyFormat
      : moneySettings.moneyFormat;
    const formatter = window.Shopify?.formatMoney || window.formatMoney;

    if (typeof formatter === 'function') {
      return formatter(cents, moneyFormat || '{{amount}}');
    }

    return (Number(cents || 0) / 100).toFixed(2);
  }

  formatMoneyFromSample(cents) {
    const sample = this.moneySample.trim();
    const sampleCents = this.moneySampleCents;
    const numericMatch = sample.match(/\d[\d.,\s\u00a0'’]*\d|\d/);

    if (!sample || !numericMatch || !sampleCents) return '';

    const amountToken = numericMatch[0];
    const lastDot = amountToken.lastIndexOf('.');
    const lastComma = amountToken.lastIndexOf(',');
    const decimalSeparatorIndex = Math.max(lastDot, lastComma);
    const digitsAfterSeparator = decimalSeparatorIndex >= 0
      ? amountToken.length - decimalSeparatorIndex - 1
      : 0;
    const hasDecimal = decimalSeparatorIndex >= 0 && digitsAfterSeparator > 0 && digitsAfterSeparator < 3;
    const decimalSeparator = hasDecimal ? amountToken[decimalSeparatorIndex] : '';
    const decimalDigits = hasDecimal ? digitsAfterSeparator : 0;
    const integerToken = hasDecimal ? amountToken.slice(0, decimalSeparatorIndex) : amountToken;
    const groupSeparatorMatch = integerToken.match(/[.,\s\u00a0'’]/);
    const groupSeparator = groupSeparatorMatch ? groupSeparatorMatch[0] : '';
    const prefix = sample.slice(0, numericMatch.index);
    const suffix = sample.slice(numericMatch.index + amountToken.length);
    const numericValue = hasDecimal
      ? (Math.round(Number(cents) || 0) / 100).toFixed(decimalDigits)
      : Math.round((Number(cents) || 0) / 100).toFixed(0);
    const parts = numericValue.split('.');
    const integerValue = parts[0];
    const decimalValue = parts[1] || '';
    const groupedInteger = groupSeparator
      ? integerValue.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
      : integerValue;
    const formattedAmount = hasDecimal
      ? `${groupedInteger}${decimalSeparator}${decimalValue}`
      : groupedInteger;

    return `${prefix}${formattedAmount}${suffix}`;
  }

  renderMoney(element, cents) {
    if (!element) return;

    const formattedMoney = this.formatMoney(cents);
    if (element.innerHTML !== formattedMoney) {
      element.innerHTML = formattedMoney;
    }
  }

  setText(element, text) {
    if (element && element.textContent !== text) {
      element.textContent = text;
    }
  }

  getActiveDiscountPercentage(hasSelectedOptional) {
    return hasSelectedOptional ? this.comboDiscountPercentage : this.bundleDiscountPercentage;
  }

  formatDiscountLabel(template, discountPercentage) {
    const text = template.trim();
    if (text.includes('[discount]')) {
      return text.replace(/\[discount\]/g, discountPercentage);
    }

    return `${text} ${discountPercentage}%`.trim();
  }

  updateDiscountCopy(discountPercentage) {
    const accessoriesDiscount = this.sectionContainer.querySelector('.bundle-accessories-discount');

    if (accessoriesDiscount && this.accessoriesDiscountText.includes('[discount]')) {
      this.setText(accessoriesDiscount, this.formatDiscountLabel(this.accessoriesDiscountText, discountPercentage));
    }

    this.requiredWrappers.forEach(wrapper => {
      const badge = wrapper.querySelector('.bundle-item-discount-badge');
      this.setText(badge, this.formatDiscountLabel(this.productDiscountText, discountPercentage));
    });
  }

  updatePriceElements(wrapper, discountPercentage) {
    const data = this.getCardData(wrapper);
    if (!data) return;

    const discountedPrice = Math.round(data.price * (1 - discountPercentage / 100));
    const compareAtPrice = Math.max(data.compareAtPrice, data.price);
    const discountedPriceEl = wrapper.querySelector('.bundle-item-discounted-price, .discounted-price');
    const originalPriceEl = wrapper.querySelector('.bundle-item-original-price, .original-price');

    this.renderMoney(discountedPriceEl, discountedPrice);
    if (originalPriceEl) {
      this.renderMoney(originalPriceEl, compareAtPrice);
      originalPriceEl.hidden = compareAtPrice <= discountedPrice;
    }
  }

  updateBundleUI() {
    let compareAtTotalCents = 0;
    let discountedTotalCents = 0;
    let selectedCount = 0;
    const hasSelectedOptional = Array.from(this.optionalWrappers).some(wrapper => {
      return wrapper.dataset.selected === 'true';
    });
    const activeDiscountPercentage = this.getActiveDiscountPercentage(hasSelectedOptional);

    this.requiredWrappers.forEach(wrapper => {
      const data = this.getCardData(wrapper);
      if (!data) return;

      compareAtTotalCents += Math.max(data.compareAtPrice, data.price);
      discountedTotalCents += Math.round(data.price * (1 - activeDiscountPercentage / 100));
      selectedCount++;
      this.updatePriceElements(wrapper, activeDiscountPercentage);
    });

    this.optionalWrappers.forEach(wrapper => {
      const isSelected = wrapper.dataset.selected === 'true';
      const discountPercentage = isSelected ? this.comboDiscountPercentage : this.bundleDiscountPercentage;
      const data = this.getCardData(wrapper);

      if (!data) return;

      this.updatePriceElements(wrapper, discountPercentage);
      if (isSelected) {
        compareAtTotalCents += Math.max(data.compareAtPrice, data.price);
        discountedTotalCents += Math.round(data.price * (1 - activeDiscountPercentage / 100));
        selectedCount++;
      }
    });

    const savingsCents = compareAtTotalCents - discountedTotalCents;

    this.updateDiscountCopy(activeDiscountPercentage);
    this.originalTotalEls.forEach(element => {
      this.renderMoney(element, compareAtTotalCents);
    });
    this.discountedTotalEls.forEach(element => {
      this.renderMoney(element, discountedTotalCents);
    });

    if (this.itemCountEl) {
      this.setText(this.itemCountEl, this.itemsCountTemplate.replace('[count]', selectedCount));
    }

    if (this.savingsBadgeEl) {
      this.renderMoney(this.savingsBadgeEl, savingsCents);
      this.savingsBadgeEl.classList.toggle('hidden', savingsCents <= 0);
    }
  }

  getCardData(wrapper) {
    if (!wrapper || !wrapper.dataset.defaultVariantId) return null;

    return {
      variantId: wrapper.dataset.defaultVariantId,
      price: Number(wrapper.dataset.defaultVariantPrice) || 0,
      compareAtPrice: Number(wrapper.dataset.defaultVariantCompareAtPrice) || 0
    };
  }

  async handleAddToCart() {
    if (!this.addToCartBtn || this.addToCartBtn.getAttribute('aria-busy') === 'true') return;

    const items = [];

    this.requiredWrappers.forEach(wrapper => {
      const data = this.getCardData(wrapper);
      if (data?.variantId) items.push({ id: data.variantId, quantity: 1 });
    });

    this.optionalWrappers.forEach(wrapper => {
      if (wrapper.dataset.selected !== 'true') return;
      const data = this.getCardData(wrapper);
      if (data?.variantId) items.push({ id: data.variantId, quantity: 1 });
    });

    if (!items.length) return;

    this.addToCartBtn.setAttribute('aria-busy', 'true');
    this.addToCartBtn.disabled = true;
    this.addToCartBtn.querySelector('.loader-button__loader')?.removeAttribute('hidden');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });
      const responseText = await response.text();
      let responseData = {};

      try {
        responseData = responseText ? JSON.parse(responseText) : {};
      } catch (error) {
        responseData = { message: responseText };
      }

      if (!response.ok) {
        throw new Error(responseData.message || responseData.description || this.cartErrorText);
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    } catch (error) {
      alert(error.message || this.cartErrorText);
    } finally {
      this.addToCartBtn.setAttribute('aria-busy', 'false');
      this.addToCartBtn.disabled = false;
      this.addToCartBtn.querySelector('.loader-button__loader')?.setAttribute('hidden', '');
    }
  }
}

const initBundleProductCards = () => {
  document.querySelectorAll('[id^="BundleSection-"]').forEach(section => {
    if (section.dataset.bundleInitialized === 'true') return;

    const sectionId = section.id.replace('BundleSection-', '');
    new BundleProductCard(sectionId);
    section.dataset.bundleInitialized = 'true';
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBundleProductCards);
} else {
  initBundleProductCards();
}

document.addEventListener('shopify:section:load', initBundleProductCards);
